import React, { useMemo } from "react";
import { filterMenuItems, formatNumber, getItems, getRank } from "../menuEngineeringUtils";
import { RankBadge } from "./menuDashboard";
import MenuGroupTabs, { useMenuGroups } from "./menuGroupTabs";

const RANKS = ["Star", "Puzzle", "Plowhorse", "Dog"];

function MultiOutletComparison({ outlets }) {
  const items = useMemo(() => getItems(outlets), [outlets]);
  const { tabs } = useMenuGroups(items, outlets);
  const { page, category } = tabs;
  const matrix = useMemo(() => {
    const itemMap = new Map();
    outlets.forEach((outlet, outletIndex) => {
      filterMenuItems(outlet.items || [], page, category).forEach((item, itemIndex) => {
        const name = item.itemDescription || `Item ${item.itemRef || itemIndex + 1}`;
        const existing = itemMap.get(name) || { name, entries: [] };
        existing.entries[outletIndex] = item;
        itemMap.set(name, existing);
      });
    });
    return [...itemMap.values()];
  }, [outlets, page, category]);

  const summary = useMemo(() => {
    const counts = RANKS.reduce((result, rank) => ({ ...result, [rank]: 0 }), {});
    matrix.forEach((row) =>
      row.entries.forEach((item) => {
        const rank = getRank(item?.rank);
        if (counts[rank] != null) counts[rank] += 1;
      })
    );
    return counts;
  }, [matrix]);

  if (!items.length) {
    return (
      <div className="menuEngineeringEmpty compact">
        <h2>No outlet data found</h2>
        <p>Try another date range or select different outlets.</p>
      </div>
    );
  }

  return (
    <div className="menuComparison">
      <MenuGroupTabs {...tabs} />
      {!matrix.length && (
        <div className="menuEngineeringEmpty compact">
          <h2>No menu items in this selection</h2>
          <p>Try another page or category.</p>
        </div>
      )}
      <section className="menuComparisonIntro">
        <div>
          <div className="menuCardEyebrow">MENU ENGINEERING / OUTLET COMPARISON</div>
          <h2>Same dish, different story</h2>
          <p>
            Compare each dish’s classification across the selected outlets.
            Each outlet keeps its own menu-engineering thresholds.
          </p>
        </div>
        <div className="menuComparisonMeta">
          <span>{formatNumber(matrix.length)} dishes</span>
          <span>{formatNumber(outlets.length)} outlets</span>
        </div>
      </section>

      <section className="menuComparisonSummary" aria-label="Classification summary">
        {RANKS.map((rank) => (
          <article className={`menuComparisonKpi rank-${rank.toLowerCase()}`} key={rank}>
            <span>{rank}s</span>
            <strong>{summary[rank]}</strong>
            <small>outlet classifications</small>
          </article>
        ))}
      </section>

      <section className="menuCard menuComparisonTable">
        <div className="menuCardHeader">
          <div>
            <h2>Outlet matrix</h2>
            <p>Classification is shown per dish and outlet.</p>
          </div>
          <div className="menuMatrixLegend">
            {RANKS.map((rank) => (
              <span key={rank}>
                <i className={`rank-dot rank-${rank.toLowerCase()}`} />
                {rank}
              </span>
            ))}
          </div>
        </div>
        <div className="menuTableScroll">
          <table>
            <thead>
              <tr>
                <th>Dish</th>
                {outlets.map((outlet, index) => (
                  <th key={outlet.chaKey || index}>
                    {outlet.name || `Outlet ${index + 1}`}
                    <small>Own thresholds</small>
                  </th>
                ))}
                <th>Group verdict</th>
              </tr>
            </thead>
            <tbody>
              {matrix.map((row) => {
                const ranks = row.entries.map((item) => getRank(item?.rank));
                const starAt = row.entries
                  .map((item, index) => (getRank(item?.rank) === "Star" ? index : -1))
                  .filter((index) => index >= 0);
                const allSame =
                  ranks.length === outlets.length &&
                  ranks.every((rank) => rank === ranks[0]);
                const verdict = allSame
                  ? `Consistent: ${ranks[0]}`
                  : starAt.length
                  ? `Star at ${outlets[starAt[0]].name || `Outlet ${starAt[0] + 1}`}`
                  : "Mixed · review by outlet";
                return (
                  <tr key={row.name}>
                    <td className="menuComparisonDish">{row.name}</td>
                    {outlets.map((outlet, index) => {
                      const item = row.entries[index];
                      return (
                        <td key={outlet.chaKey || index}>
                          {item ? <RankBadge rank={getRank(item.rank)} /> : <span className="menuMissing">No data</span>}
                        </td>
                      );
                    })}
                    <td className="menuGroupVerdict">{verdict}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      <p className="menuComparisonNote">
        Rankings are based on each outlet’s returned menu-engineering results;
        missing dishes are not assigned a classification.
      </p>
    </div>
  );
}

export default MultiOutletComparison;
